import { useEditor, EditorContent } from "@tiptap/react";
import { useEffect, useRef } from "react";
import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { createLowlight } from "lowlight";
// Highlight.js languages
import javascript from "highlight.js/lib/languages/javascript";
import html from "highlight.js/lib/languages/xml";
// Extensions
import { FontSize } from "./extensions/FontSize";
import { FontFamily } from "./extensions/FontFamily";
import Image from "@tiptap/extension-image";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { projectStorage } from "../../firebase/config"; // update path as needed
import { v4 as uuidv4 } from "uuid"; // for unique filenames

// Font families
const fonts = [
  // Sans-serif
  "Arial, sans-serif",
  "Calibri, sans-serif",
  "Candara, sans-serif",
  "Futura, sans-serif",
  "Geneva, sans-serif",
  "Helvetica, sans-serif",
  "Segoe UI, sans-serif",
  "Tahoma, sans-serif",
  "Trebuchet MS, sans-serif",
  "Verdana, sans-serif",
  // Serif
  "Baskerville, serif",
  "Book Antiqua, serif",
  "Cambria, serif",
  "Constantia, serif",
  "Didot, serif",
  "Garamond, serif",
  "Georgia, serif",
  "Palatino Linotype, serif",
  "Times New Roman, serif",
  // Monospace
  "Courier New, monospace",
  "Lucida Console, monospace",
  "Monaco, monospace",
  "Consolas, monospace",
  // Cursive
  "Brush Script MT, cursive",
  "Comic Sans MS, cursive",
  "Lucida Handwriting, cursive",
  // Fantasy
  "Impact, fantasy",
  "Jokerman, fantasy",
  "Papyrus, fantasy",
];

// Font sizes
const fontSizes = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "48px",
  "60px",
  "72px",
];

// RichTextEditor component using Tiptap
// This component provides a rich text editor with various formatting options
const RichTextEditor = ({ value, onChange }) => {
  // Create a lowlight instance for syntax highlighting
  const lowlight = createLowlight();
  // Register languages for code blocks
  lowlight.register("js", javascript);
  lowlight.register("html", html);

  const hasSetContent = useRef(false); // prevent infinite loop

  // Initialize the Tiptap editor with extensions
  // This includes basic formatting options like bold, italic, underline,
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Underline,
      FontSize,
      FontFamily,
      CodeBlockLowlight.configure({ lowlight }),
      BulletList,
      OrderedList,
      ListItem,
      Image,
    ],
    content: "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Load content when ready
  useEffect(() => {
    if (editor && value && !hasSetContent.current) {
      editor.commands.setContent(value);
      hasSetContent.current = true;
    }
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div>
      <div className="toolbar mb-2 d-flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={
            editor.isActive("bold") ? "btn btn-light" : "btn btn-outline-secondary text-light"
          }
        >
          Bold
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={
            editor.isActive("italic") ? "btn btn-light" : "btn btn-outline-secondary text-light"
          }
        >
          Italic
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={
            editor.isActive("underline") ? "btn btn-light" : "btn btn-outline-secondary text-light"
          }
        >
          Underline
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={
            editor.isActive("bulletList") ? "btn btn-light" : "btn btn-outline-secondary text-light"
          }
        >
          Bullet List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={
            editor.isActive("orderedList")
              ? "btn btn-light"
              : "btn btn-outline-secondary text-light"
          }
        >
          Numbered List
        </button>

        <div className="d-flex gap-2">
          <select
            onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
            className="form-select form-select-sm bg-dark text-light border-secondary"
          >
            <option value="">Font Size</option>
            {fontSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>

          <select
            onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
            className="form-select form-select-sm bg-dark text-light border-secondary"
          >
            <option value="">Font Family</option>
            {fonts.map((font) => (
              <option key={font} value={font}>
                {font.split(",")[0]}
              </option>
            ))}
          </select>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={(ref) => (window.uploadInputRef = ref)}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;

              const storageRef = ref(projectStorage, `editorImages/${uuidv4()}-${file.name}`);
              await uploadBytes(storageRef, file);
              const url = await getDownloadURL(storageRef);

              editor.chain().focus().setImage({ src: url }).run();
            }}
          />
          <button
            type="button"
            className="btn btn-outline-secondary text-light"
            onClick={() => window.uploadInputRef && window.uploadInputRef.click()}
          >
            Upload Image
          </button>
        </div>
      </div>

      <EditorContent editor={editor} className="border p-2 bg-secondary" />
    </div>
  );
};

export default RichTextEditor;
